import React, { useEffect, useState } from 'react';
import AuthenticatedApp from './components/authenticated/authenticated-app';
import UnauthenticatedApp from './components/unauthenticated/unauthenticated-app';
import createAppStateStore from './app-state-store';
import { cetraLightModeTheme } from './theme';
import { observer } from 'mobx-react-lite';
import AppAuthState from './react-core-lib/components/global/AuthState';
import ContentManager from './components/state-mgmt/content-manager';
import AuthenticatedAppWithStore from './react-core-lib/components/app/authenticated-app-with-store';
import configureAmplify from './react-core-lib/third-party/amplify';
import mixpanel from 'mixpanel-browser';
import ReactGA4 from 'react-ga4';
import './App.css';
import './console-overrides';
import { register } from 'swiper/element/bundle';
import AccessibilityWidget from './AccessibilityWidget';
import config from './config';

register();
configureAmplify();

function App() {
    // SUpport to help function to capture and update ProVet params
    const captureProvetParams = () => {
        try {
            const sp = new URLSearchParams(window.location.search || '');
            const c = sp.get('client');
            const u = sp.get('user');
            const ts = sp.get('timestamp');
            const v = sp.get('verification');

            // If any ProVet params are present, clear old ones first and set new ones
            if (c || u || ts || v) {
                console.log('[ProVet] New parameters detected, updating localStorage...');
                // Clear old params
                localStorage.removeItem('provet_client');
                localStorage.removeItem('provet_user');
                localStorage.removeItem('provet_timestamp');
                localStorage.removeItem('provet_verify');

                // Set new params
                if (c) localStorage.setItem('provet_client', c);
                if (u) localStorage.setItem('provet_user', u);
                if (ts) localStorage.setItem('provet_timestamp', ts);
                if (v) localStorage.setItem('provet_verify', v);
            }
        } catch (_) {
            /* noop */
        }
    };

    useEffect(() => {
        // Initial capture on mount
        captureProvetParams();

        const { clinicType, isProd, mixpanelToken, gtagId } = config;

        if (mixpanelToken) {
            mixpanel.init(mixpanelToken, {
                verbose: true,
                debug: true,
                track_pageview: 'url-with-path',
                persistence: 'localStorage',
                ignore_dnt: true,
            });
            console.log(`Mixpanel initialized with token: ${mixpanelToken}`, clinicType, isProd);
        }

        if (gtagId) {
            ReactGA4.initialize(gtagId);
        }

        // Listen for URL changes (e.g., when ProVet navigates to new client)
        const handleLocationChange = () => {
            captureProvetParams();
        };

        // Listen for tab visibility/focus changes (in case ProVet updates URL when switching)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                captureProvetParams();
            }
        };

        const handleFocus = () => {
            captureProvetParams();
        };

        // Add event listeners
        window.addEventListener('popstate', handleLocationChange);
        window.addEventListener('hashchange', handleLocationChange);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        // Cleanup
        return () => {
            window.removeEventListener('popstate', handleLocationChange);
            window.removeEventListener('hashchange', handleLocationChange);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, []);

    return (
        <>
            <AuthenticatedAppWithStore
                AuthenticatedComponent={AuthenticatedApp}
                UnauthenticatedComponent={UnauthenticatedApp}
                createAppStateStore={createAppStateStore}
                theme={cetraLightModeTheme}
                globalComponents={
                    <>
                        <AppAuthState />
                        <AccessibilityWidget />
                    </>
                }
                globalAuthenticatedComponents={<ContentManager />}
            />
        </>
    );
}

export default observer(App);
